import React, { useState, useEffect } from 'react';
import { normalizeUrl, handleImgError } from '../../../utils/imageUtils';
import { 
  getCurrentConfig, 
  transformImageUrlForProduction, 
  productionLog, 
  productionError 
} from '../../../utils/productionConfig';
import Logo from '../../../assets/images/common/importerr-no-image-logo.png';
import './style.css';

const AlibabaImage = ({ 
  src, 
  alt = '', 
  className = '', 
  style = {}, 
  width = 'auto', 
  height = 'auto',
  fallbackSrc = Logo,
  showLoadingState = true,
  retryCount = 3
}) => {
  const [imageSrc, setImageSrc] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retryAttempts, setRetryAttempts] = useState(0);
  const config = getCurrentConfig();

  useEffect(() => {
    if (src) {
      // Reset loading state when src changes
      setIsLoading(true);
      setHasError(false);
      setRetryAttempts(0);
      loadImage(src);
    }
  }, [src]);

  const loadImage = (url) => {
    setIsLoading(true);
    setHasError(false);
    
    console.log('🔄 Starting to load image:', url);
    
    // Use production-specific URL transformation
    const transformedUrl = transformImageUrlForProduction(url);
    const normalizedUrl = normalizeUrl(transformedUrl);
    
    console.log('🔄 URL transformation:', { 
      original: url, 
      transformed: transformedUrl, 
      normalized: normalizedUrl 
    });
    
    // Special handling for CloudFront URLs - extract original Alibaba URL
    if (normalizedUrl.includes('cloudfront.net')) {
      console.log('🌐 CloudFront URL detected, attempting to extract original Alibaba URL...');
      
      // Try to extract original URL from the CloudFront path
      // Pattern: translated-images/20251001_150221_ORIGINAL_FILENAME_translated_en.jpg
      const cloudfrontMatch = normalizedUrl.match(/translated-images\/\d+_\d+_(.+)_translated_en\.jpg$/);
      if (cloudfrontMatch) {
        const originalFilename = cloudfrontMatch[1];
        const originalUrl = `https://cbu01.alicdn.com/img/ibank/${originalFilename}`;
        console.log('🔄 Extracted original filename:', originalFilename);
        console.log('🔄 Using original Alibaba URL to avoid CORS issues:', originalUrl);
        
        // Use original Alibaba URL as primary source to avoid CORS issues
        setImageSrc(originalUrl);
        
        return; // Exit early, we're handling this case specially
      } else {
        console.log('❌ Could not extract original URL from CloudFront path');
      }
    }
    
    // Special handling for proxy URLs (allorigins.win) - try direct URL first
    if (normalizedUrl.includes('allorigins.win')) {
      console.log('🌐 Proxy URL detected, trying direct URL first...');
      
      // Extract the original URL from the proxy
      const urlMatch = normalizedUrl.match(/url=(.+)$/);
      if (urlMatch) {
        const directUrl = decodeURIComponent(urlMatch[1]);
        console.log('🔄 Extracted direct URL:', directUrl);
        
        // Try the direct URL first
        setImageSrc(directUrl);
        
        // Also set up a fallback to the proxy URL if direct fails
        const img = new Image();
        img.onload = () => {
          setIsLoading(false);
          setHasError(false);
          console.log('✅ Direct URL loaded successfully:', directUrl);
        };
        img.onerror = () => {
          console.log('❌ Direct URL failed, falling back to proxy URL:', normalizedUrl);
          setImageSrc(normalizedUrl);
        };
        img.src = directUrl;
        return;
      }
    }
    
    productionLog('Loading image', { original: url, transformed: transformedUrl, normalized: normalizedUrl });
    
    setImageSrc(normalizedUrl);
    
    // Create a new image to test loading
    const img = new Image();
    
    // Set referrerPolicy for all images
    try { img.referrerPolicy = 'no-referrer'; } catch {}
    
    // Don't set crossOrigin - it can cause CORS issues with CDN images
    
    // Add timeout for CloudFront URLs
    let timeout;
    if (normalizedUrl.includes('cloudfront.net')) {
      timeout = setTimeout(() => {
        console.log('⏰ CloudFront image timeout:', normalizedUrl);
        if (isLoading) {
          console.log('⏰ CloudFront image timed out, triggering error handler');
          handleImageError(normalizedUrl);
        }
      }, 10000); // 10 second timeout for CloudFront
    }
    
    img.onload = () => {
      if (timeout) clearTimeout(timeout);
      setIsLoading(false);
      setHasError(false);
      productionLog('Image loaded successfully', { url: normalizedUrl });
      console.log('✅ Image loaded successfully:', normalizedUrl);
    };
    
    img.onerror = (error) => {
      if (timeout) clearTimeout(timeout);
      productionError('Image load failed', { url: normalizedUrl, originalUrl: url, error });
      console.error('❌ Image load failed:', normalizedUrl, error);
      handleImageError(normalizedUrl);
    };
    
    img.src = normalizedUrl;
  };

  const handleImageError = (failedUrl) => {
    productionLog(`Image error (attempt ${retryAttempts + 1})`, { url: failedUrl, retryCount: retryAttempts });
    console.log(`❌ Image error (attempt ${retryAttempts + 1}):`, failedUrl);
    
    if (retryAttempts < config.imageLoading.retryAttempts) {
      let retryUrl;
      
      if (failedUrl.includes('cloudfront.net')) {
        // For CloudFront URLs, try to find the original Alibaba URL as fallback
        console.log('🔄 CloudFront image failed, checking for original Alibaba URL fallback');
        
        // Try to extract original URL from the CloudFront path
        // The CloudFront URL format seems to be: .../translated-images/20251001_150221_ORIGINAL_ALIBABA_FILENAME_translated_en.jpg
        const cloudfrontMatch = failedUrl.match(/translated-images\/\d+_\d+_(.+)_translated_en\.jpg$/);
        if (cloudfrontMatch) {
          const originalFilename = cloudfrontMatch[1];
          // Try to construct the original Alibaba URL
          const originalUrl = `https://cbu01.alicdn.com/img/ibank/${originalFilename}`;
          console.log('🔄 Trying original Alibaba URL as fallback:', originalUrl);
          retryUrl = originalUrl;
        } else {
          // Just retry with the same CloudFront URL
          console.log('🔄 Retrying CloudFront URL without modification');
          retryUrl = failedUrl;
        }
      } else if (failedUrl.includes('allorigins.win')) {
        // For proxy URLs, try the direct URL
        console.log('🔄 Proxy URL failed, trying direct URL');
        const urlMatch = failedUrl.match(/url=(.+)$/);
        if (urlMatch) {
          const directUrl = decodeURIComponent(urlMatch[1]);
          console.log('🔄 Trying direct URL as fallback:', directUrl);
          retryUrl = directUrl;
        } else {
          // Just retry with the same proxy URL
          console.log('🔄 Retrying proxy URL without modification');
          retryUrl = failedUrl;
        }
      } else {
        // For other URLs, use cache busting strategies
        const baseUrl = failedUrl.split('?')[0];
        const timestamp = Date.now();
        const randomParam = Math.random().toString(36).substring(7);
        retryUrl = `${baseUrl}?t=${timestamp}&v=${retryAttempts + 1}&r=${randomParam}&retry=${retryAttempts + 1}`;
        console.log('🔄 Retrying with modified URL:', retryUrl);
      }
      
      productionLog('Retrying with URL', { retryUrl, attempt: retryAttempts + 1 });
      
      setTimeout(() => {
        setRetryAttempts(prev => prev + 1);
        loadImage(retryUrl);
      }, config.imageLoading.retryDelay * (retryAttempts + 1));
    } else {
      // Use fallback
      productionLog('Using fallback image', { originalUrl: failedUrl, fallbackSrc });
      console.log('🔄 Using fallback image for:', failedUrl);
      setImageSrc(fallbackSrc);
      setIsLoading(false);
      setHasError(true);
    }
  };

  const handleImgErrorFallback = (e) => {
    e.target.src = fallbackSrc;
    e.target.onerror = null;
    setHasError(true);
  };

  const containerStyle = {
    position: 'relative',
    width: width,
    height: height,
    overflow: 'hidden',
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    ...style
  };

  const imageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
    display: isLoading ? 'none' : 'block'
  };

  return (
    <div className={`alibaba-image-container ${className}`} style={containerStyle}>
      {isLoading && showLoadingState && (
        <div className="image-loading">
          <div className="loading-spinner"></div>
          <span>Loading...</span>
        </div>
      )}
      
      {hasError && (
        <div className="image-error">
          <img 
            src={fallbackSrc} 
            alt={alt} 
            style={imageStyle}
            onError={handleImgErrorFallback}
          />
          <div className="error-overlay">
            <span>Image unavailable</span>
          </div>
        </div>
      )}
      
      {!hasError && (
        <img 
          src={imageSrc} 
          alt={alt} 
          style={imageStyle}
          onError={handleImgErrorFallback}
          referrerPolicy="no-referrer"
        />
      )}
    </div>
  );
};

export default AlibabaImage;
